import { Injectable } from '@angular/core';
import { Actions, DealAbstractService, HitTypes, IEvent } from 'bqx-deal';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { Global } from 'src/app/models/shared/app-global.model';
import { Section } from 'src/app/models/shared/section.model';
import { AbstractAuthService } from 'src/app/services/auth/auth.abstract.service';
import { ApplicationFormAbstractService } from '../application-form/application-form.abstract.service';
import { AbstractDealAnalyticsService } from './abstract-deal-analytics.service';

export const CustomDimension = {
  category: 'Business Quote Explorer',
  channel: 'AG',
  product: 'BI',
  userType: 'Agent'
};

@Injectable({
  providedIn: 'root'
})
export class DealAnalyticsService implements AbstractDealAnalyticsService {
  private _privacy: undefined;
  private _currentUrl: string;
  private _currentPage: string;
  private _currentTitle: string;
  
  constructor(
    private dealService: DealAbstractService,
    private applicationFormService: ApplicationFormAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger,
    private authService: AbstractAuthService
  ) {
    this._currentUrl = window.location.href;
  }

  get currentSection(): Section {
    let section: Section;
    if (sessionStorage.getItem('currSec') == 'undefined' || sessionStorage.getItem('currSec') == null) {
      section =  this.createSectionData();
    }
    else {
	    section = JSON.parse(sessionStorage.getItem('currSec'));
	  }
    return section;
  }

  get quoteNumber (): string {
    const quoteNbr: string = this.applicationFormService.quoteNumber;
    return quoteNbr?.replaceAll('-', '');
  }

  get state (): string {
    return this.applicationFormService.stateCode;
  }

  get product (): string {
    return this.applicationFormService.riskCode;
  }

  get isRetrieval(): boolean {
    return sessionStorage.getItem('isQuoteRetrieval') === 'true' ? true : false;
  }
    
  private get activeQuestionSet(): IQuestionSet {
    return this.applicationFormService.activeQuestionSet$.value;    
  }

  public postDealPageEvent(page?: string, title?: string, handshakeGUID?: string): void {
    try {
      const section: Section = this.createSectionData(page, title, handshakeGUID);
      sessionStorage.setItem('currSec', JSON.stringify(section));
      const currentDealEventData: IEvent = this.createEventData(section);
      currentDealEventData.action = Actions.PageLoad;
      this._currentUrl = window.location.href;
      this._currentPage = section?.route;
      this._currentTitle = section?.title;
      if(this._currentPage) {
        this.dealService.postEvent(
          currentDealEventData,
          HitTypes.Page,
          this._currentUrl,
          this._currentPage,
          this._currentTitle,
          this._privacy
        );
        this.loggerService.postLogData(
          LogLevel.Info,
          'Page Hit Event',
          JSON.stringify({
            page: currentDealEventData.dimensions?.carrierName ?? this._currentTitle,
            quoteNumber: this.applicationFormService.quoteNumber ?? Global.applicationId
          })
        );
      }
    } catch (e) {
      this.loggerService.postLogData(LogLevel.Error, 'DealAnalytics-service: postDealPageEvent', JSON.stringify(e, Object.getOwnPropertyNames(e)), e.stack);
    }
  }

  public postDealControlEvent(action: Actions, label: string, page?: string, title?: string, carrierName?: string, dataValue?: string, premiumAmount?: string): void {
    try {
      const section: Section = this.currentSection;
      const currentDealEventData: IEvent = this.createEventData(section);
      this._currentUrl = window.location.href;
      this._currentPage = page ? page : section?.route;
      this._currentTitle = title ? title : section?.title;
      currentDealEventData.label = label?.replaceAll('’', '\'');
      currentDealEventData.action = action;
      currentDealEventData.dimensions.carrierName = carrierName?.replaceAll(' ', '');
      currentDealEventData.dimensions.dataValue = dataValue;
      currentDealEventData.dimensions.carrierPremiumAmount = premiumAmount;
      this.dealService.postEvent(
        currentDealEventData,
        HitTypes.Event,
        this._currentUrl,
        this._currentPage,
        this._currentTitle,
        this._privacy
      );
    } catch (e) {
      this.loggerService.postLogData(LogLevel.Error, 'DealAnalytics-service: postDealControlEvent', JSON.stringify(e, Object.getOwnPropertyNames(e)), e.stack);
    }
  }

  private truncateUserRoles(userRoles: string): string {
    let formatedRoles;

    if (userRoles){
      const resultArray = userRoles.split(",");
      formatedRoles =  resultArray.map(role => this.formatRole(role))?.join(',');
    }
    return formatedRoles;
  }

  private createEventData(section: Section): IEvent {
    const dealEvent: IEvent = {
      category: CustomDimension.category,
      dimensions: {
        httpStatus: '200',
        dataValue: null,
        userType: CustomDimension.userType,
        LanID: this.authService.currentUserLanId,
        user: {
          title: this.truncateUserRoles(this.authService.currentUserRoles),
        },
        handshakeGUID: section?.handshakeGUID,
        intRepInd: true,
        carrierName: section?.carrierName,
        quoteRtrvl: String(this.isRetrieval),
        quotes: [
          {
            channel: CustomDimension.channel,
            product: CustomDimension.product,
            quoteNbr: this.quoteNumber,
            riskType: this.product,
            state: this.state,
            quoteSource: null
          }
        ],
      },
      milestoneName: ''
    };
    return dealEvent;
  }

  private createSectionData(page?: string, title?: string, handshakeGUID?: string): Section {
    if(page && title) {
      return new Section(title, page, handshakeGUID);
    }
    let section: Section;
    const url = window.location.href.substring(window.location.href.lastIndexOf('/') + 1)
      switch(url) {
        case 'dashboard': {
          section = new Section('Dashboard', '/dashboard');
          break;
        }
        case 'search_forms': {
          section = new Section('Search', '/search_forms');
          break;
        }
    }
  if (!section){
      if(this.activeQuestionSet) {
        switch (this.activeQuestionSet.question_set?.title.toLowerCase()) {
          case 'coverage':
          case 'business type':
          case 'contact': {
            if(this.quoteNumber) {
              section = new Section('Business Info', '/application_form');
            }
            else {
              section = new Section('Business Info', '/application_form', Global.applicationId);
            }
            break;
          }
          case 'mqs':
          case 'mqs-business details':
          case 'mqs-building information':
          case 'consent':
          case 'general': {
            section = new Section(this.activeQuestionSet.question_set.title, '/application_form', handshakeGUID);
            break;
          }
          default: {
            section = new Section('Advanced',
              '/application_form',
              undefined,
              this.activeQuestionSet.question_set?.title?.replaceAll(' ', ''));
            }
          }
      }
      else if (this.applicationFormService.isAppFormRedirected$.value){
        section = new Section('Carrier Results','/application_form');
      }
    }
    return section;
  }

  private formatRole(role: string){
    let formattedRole;
    if( role) {
      formattedRole = role.trim().replace('[', '').replace(']', '').substring(4);
    }
    return formattedRole;
  }
}
